<script setup lang="ts">
import { ref, computed, inject, onMounted } from "vue";
import { TranslationService } from "../services/translation.service";
import { SITEURL } from "@libTs/cms.globals";
import axios from "axios";

const t = (inject<TranslationService>("translationSvc") as TranslationService)
    .t;

const { description, properties } = defineProps({
    description: {
        type: String,
        required: false,
        default: null,
    },
    properties: {
        type: Object,
        required: true,
    },
    dsstoxUrl: {
        type: String,
        required: true,
    },
    casUrl: {
        type: String,
        required: true,
    },
});

const openItems = ref([] as string[]);
const siteUrl = SITEURL;
const hasSDF = ref(false);

onMounted(() => {
    if (description) {
        openItems.value.push("Description");
    } else {
        openItems.value.push("Properties");
    }

    // check if SDF is available
    axios
        .get(
            `${siteUrl}shop/check-sdf/${encodeURIComponent(properties.item.ItemCode)}`,
        )
        .then(
            (res) => {
                hasSDF.value = res.data.exists;
            },
            (err) => {
                // set "not found" status
                hasSDF.value = false;
                console.error(err);
            },
        );
});

const toggleItem = (item: string) => {
    if (openItems.value.includes(item)) {
        openItems.value = openItems.value.filter((i) => i !== item);
    } else {
        openItems.value.push(item);
    }
};
</script>

<template>
    <h2
        class="mb-10"
        v-if="description || (properties && Object.keys(properties).length)"
    >
        Product Details
    </h2>
    <div class="">
        <div
            class="py-4 px-8 bg-white rounded-lg mb-8 shadow"
            v-if="description"
        >
            <div
                class="flex justify-between items-center cursor-pointer"
                @click="toggleItem('Description')"
            >
                <h3 class="text-f4 font-bold">Description</h3>
                <span>
                    <svg
                        v-if="openItems.includes('Description')"
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6 transform rotate-180 transition-transform duration-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="#009aa7"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                    <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6 transform transition-transform duration-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="#009aa7"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </span>
            </div>
            <div
                v-if="openItems.includes('Description')"
                class="mt-6 text-gray-700 transition-all duration-300 ease-in-out"
            >
                <div class="redactor-content" v-html="description"></div>
            </div>
        </div>

        <div
            class="py-4 px-8 bg-white rounded-lg mb-8 shadow"
            v-if="properties && Object.keys(properties).length"
        >
            <div
                class="flex justify-between items-center cursor-pointer"
                @click="toggleItem('Properties')"
            >
                <h3 class="text-f4 font-bold">Properties</h3>
                <span>
                    <svg
                        v-if="openItems.includes('Properties')"
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6 transform rotate-180 transition-transform duration-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="#009aa7"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                    <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-6 w-6 transform transition-transform duration-300"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="#009aa7"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                </span>
            </div>
            <div
                v-if="openItems.includes('Properties')"
                class="mt-6 text-gray-700 transition-all duration-300 ease-in-out"
            >
                <div>
                    <table class="w-full table-auto border-collapse">
                        <tbody>
                            <tr
                                class="border-b border-grey-100"
                                v-if="properties.item.Packsize"
                            >
                                <td
                                    class="py-3 pr-4 font-semibold align-top w-1/3"
                                >
                                    Packaging unit
                                </td>
                                <td
                                    v-if="properties.item.Packsize"
                                    class="py-3"
                                    v-html="properties.item.Packsize"
                                ></td>
                            </tr>
                            <tr
                                class="border-b border-grey-100"
                                v-if="properties.item.CAS"
                            >
                                <td
                                    class="py-3 pr-4 font-semibold align-top w-1/3"
                                >
                                    CAS-Number
                                </td>
                                <td v-if="properties.item.CAS" class="py-3">
                                    <a
                                        :href="
                                            '/shop?q=' +
                                            properties.item.CAS +
                                            '&size=n_20_n&filters%5B0%5D%5Bfield%5D=ExactCas&filters%5B0%5D%5Bvalues%5D%5B0%5D=b_true_b&filters%5B0%5D%5Btype%5D=all'
                                        "
                                        target="_blank"
                                        class="underline"
                                    >
                                        {{ properties.item.CAS }}
                                    </a>
                                </td>
                            </tr>

                            <tr
                                class="border-b border-grey-100"
                                v-if="
                                    properties.casData &&
                                    properties.casData.formula
                                "
                            >
                                <td
                                    class="py-3 pr-4 font-semibold align-top w-1/3"
                                >
                                    Molecular Formula
                                </td>
                                <td
                                    v-if="
                                        properties.casData &&
                                        properties.casData.formula
                                    "
                                    class="py-3"
                                    v-html="
                                        properties.casData.formula.replace(
                                            /(\w)(\d+)/gi,
                                            '$1<sub>$2</sub>',
                                        )
                                    "
                                ></td>
                            </tr>

                            <tr
                                class="border-b border-grey-100"
                                v-if="
                                    properties.casData &&
                                    properties.casData.dsstox_substance_id
                                "
                            >
                                <td
                                    class="py-3 pr-4 font-semibold align-top w-1/3"
                                >
                                    DSSTox Substance ID
                                </td>
                                <td class="py-3">
                                    <a
                                        class="flex items-center gap-1 justify-between"
                                        :href="
                                            dsstoxUrl +
                                            properties.casData
                                                .dsstox_substance_id
                                        "
                                    >
                                        <span class="underline">
                                            {{
                                                properties.casData
                                                    .dsstox_substance_id || "-"
                                            }}
                                        </span>
                                        <i
                                            class="fa fa-arrow-right text-primary"
                                        ></i>
                                    </a>
                                </td>
                            </tr>
                            <tr
                                class="border-b border-grey-100"
                                v-if="
                                    properties.casData &&
                                    properties.casData.dsstox_compound_id
                                "
                            >
                                <td
                                    class="py-3 pr-4 font-semibold align-top w-1/3"
                                >
                                    DSSTox Compound ID
                                </td>
                                <td class="py-3">
                                    <a
                                        class="flex items-center gap-1 justify-between"
                                        :href="
                                            dsstoxUrl +
                                            properties.casData
                                                .dsstox_compound_id
                                        "
                                    >
                                        <span class="underline">
                                            {{
                                                properties.casData
                                                    .dsstox_compound_id || "-"
                                            }}
                                        </span>
                                        <i
                                            class="fa fa-arrow-right text-primary"
                                        ></i>
                                    </a>
                                </td>
                            </tr>

                            <tr
                                class="border-b border-grey-100"
                                v-if="
                                    properties.casData &&
                                    properties.casData.monoisotopic_mass
                                "
                            >
                                <td
                                    class="py-3 pr-4 font-semibold align-top w-1/3"
                                >
                                    Monoisotopic Mass
                                </td>
                                <td class="py-3">
                                    {{ properties.casData.monoisotopic_mass }}
                                    g/mol
                                </td>
                            </tr>
                            <tr
                                class="border-b border-grey-100"
                                v-if="
                                    properties.casData &&
                                    properties.casData.molecular_weight
                                "
                            >
                                <td
                                    class="py-3 pr-4 font-semibold align-top w-1/3"
                                >
                                    Molecular Weight
                                </td>
                                <td class="py-3">
                                    {{ properties.casData.molecular_weight }}
                                    g/mol
                                </td>
                            </tr>
                            <tr
                                class="border-b border-grey-100"
                                v-if="
                                    properties.casData &&
                                    properties.casData.smiles
                                "
                            >
                                <td
                                    class="py-3 pr-4 font-semibold align-top w-1/3"
                                >
                                    Smiles
                                </td>
                                <td class="py-3">
                                    {{ properties.casData.smiles || "-" }}
                                </td>
                                <td v-if="hasSDF">
                                    <a
                                        class="flex items-center gap-1"
                                        :href="
                                            siteUrl +
                                            'shop/sdf/' +
                                            properties.item.ItemCode
                                        "
                                    >
                                        <span class="underline">SDF</span>
                                        <i
                                            class="fa fa-download text-primary"
                                        ></i>
                                    </a>
                                </td>
                            </tr>
                            <tr
                                class="border-b border-grey-100"
                                v-if="
                                    properties.casData &&
                                    properties.casData.in_chi
                                "
                            >
                                <td
                                    class="py-3 pr-4 font-semibold align-top w-1/3"
                                >
                                    InChi
                                </td>
                                <td class="py-3 break-all">
                                    {{ properties.casData.in_chi }}
                                </td>
                            </tr>
                            <tr
                                class=""
                                v-if="
                                    properties.casData &&
                                    properties.casData.in_chi_key
                                "
                            >
                                <td
                                    class="py-3 pr-4 font-semibold align-top w-1/3"
                                >
                                    InChi Key
                                </td>
                                <td class="py-3">
                                    {{ properties.casData.in_chi_key }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>
